/*
 * Environment details
 *
 * Contains information about the current environment.
 *
 * @property name The name of the Environment (Chrome, FF, Node, etc)
 * @property version Usually a Float, except in Node which uses a String
 * @property userAgent The raw user agent
 * @property isiOS Whether the client is on iOS
 * @property isElectron Whether the client is Electron
 * @property isOpera Whether the client is Opera
 * @property isFirefox Whether the client is Firefox
 * @property isEdge Whether the client is isEdge (legacy or chromiumedge)
 * @property isChromiumEdge Whether the client is isChromiumEdge (version >= 79)
 * @property isLegacyEdge Whether the client is isLegacyEdge (version < 79)
 * @property isLegacyElectron Whether the client is isLegacyElectron,
 * used for testing purposes (version < 7)
 * @property isLegacyChrome Whether the client is isLegacyChrome,
 * used for testing purposes (version < 87)
 * @property isAndroid Whether the client is an Android device
 * @property protocol The protocol property of the URL
 * @property hostName The domain of the URL
 */

import { isiPhone, isiPad, isiOS } from './isiOS';
import { isProd, isDev, isRel } from './isEnv';
import iOSVersion from './iOSVersion';
import getElectronVersion from './get-electron-version';

const isElectron = (navigator) => {
  // eslint-disable-next-line no-param-reassign
  navigator = navigator || global?.navigator;

  return navigator?.userAgent.toLowerCase().indexOf('electron') >= 0;
};

const isNode = () => typeof global?.process?.versions?.node === 'string' && !isElectron();

let env;

const resetEnv = () => {
  env = {
    // @todo why is the default -1?;
    version: -1,
    name: 'unknown',
    platform: 'unknown',
  };
};

const parseNode = () => {
  env.name = 'Node';
  env.isNode = true;
  env.version = parseInt(process.versions.node, 10);
  env.userAgent = `${env.name} ${env.version}`;
  env.platform = 'Node';
};

const parseBrowser = (navigator) => {
  // eslint-disable-next-line global-require, no-param-reassign
  navigator = navigator || global.navigator;

  env.userAgent = navigator.userAgent.toLowerCase(); // @todo why lowercase?
  env.name = 'unknown';

  // Detect platform based on user agent
  if (env.userAgent.indexOf('windows') > -1 || env.userAgent.indexOf('win32') > -1 || env.userAgent.indexOf('win64') > -1) {
    env.platform = 'Windows';
  } else if (env.userAgent.indexOf('mac os') > -1 || env.userAgent.indexOf('macintosh') > -1) {
    env.platform = 'macOS';
  } else if (env.userAgent.indexOf('linux') > -1) {
    env.platform = 'Linux';
  }

  if (env.userAgent.indexOf('opera') > -1 || env.userAgent.indexOf('opr/') > -1) {
    env.name = 'Opera';
    env.isOpera = true;

    if (/opr\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      env.version = parseFloat(RegExp.$1);
    }
  } else if (env.userAgent.indexOf('SamsungBrowser') > -1) {
    env.name = 'Samsung';
    env.isSamsung = true;

    if (/SamsungBrowser\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      env.version = parseFloat(RegExp.$1);
    }
  } else if (env.userAgent.indexOf('firefox') > -1) {
    env.name = 'Firefox';
    env.isFirefox = true;

    if (/firefox\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      env.version = parseFloat(RegExp.$1);
    }
  } else if (env.userAgent.match(/edge?\/(\d+)(?:\.\d+)+$/)) {
    env.name = 'Edge';
    env.isEdge = true;

    const version = RegExp.$1;

    env.version = parseInt(version, 10);
  } else if (env.userAgent.indexOf('chrome') > -1) {
    const isAndroidWebView = env.userAgent.match(/android\s(\d[.\d]{0,}).*;\s*wv\).*version\/\d\.\d\s+chrome\/(\d+\.\d+)/);
    if (isAndroidWebView) {
      env.name = 'Android_WebView';
      env.isAndroidWebView = true;
      env.isChrome = true;
      env.androidVersion = parseFloat(isAndroidWebView[1]);
      env.version = parseFloat(isAndroidWebView[2]);
    } else {
      env.name = 'Chrome';
      env.isElectron = isElectron(navigator);

      if (!env.isElectron) {
        env.isChrome = true;
      } else {
        env.electronVersion = getElectronVersion(navigator.userAgent);
      }

      if (/chrome\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
        env.version = parseFloat(RegExp.$1);
      }
    }
  } else if (navigator.vendor && navigator.vendor.toLowerCase().indexOf('apple') > -1) {
    // From https://stackoverflow.com/questions/4460205/detect-ipad-iphone-webview-via-javascript
    if (env.userAgent.indexOf('applewebkit') > -1 && env.userAgent.indexOf('version') === -1) {
      env.name = 'iOS_WebView';
      env.isIosWebView = true;
    } else {
      env.name = 'Safari';
    }
    env.isSafari = true;
    env.isiPhone = isiPhone(navigator.userAgent);
    env.isiPad = isiPad(navigator.userAgent);
    env.isiOS = isiOS(navigator.userAgent);
    if (env.isiOS) {
      env.iOSVersion = iOSVersion(navigator.userAgent);
    }
    if (/version\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      env.version = parseFloat(RegExp.$1);
    } else if (/fxios\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      // This gets the version from Firefox in iOS
      env.version = parseFloat(RegExp.$1);
    } else if (/crios\/([0-9]{1,}[.0-9]{0,})/.exec(env.userAgent) !== null) {
      // This gets the version from Chrome in iOS
      env.version = parseFloat(RegExp.$1);
    } else if (env.isiOS) {
      env.version = env.iOSVersion;
    }
  }

  // The Chromium-based Edge was released on this version
  env.isChromiumEdge = env.name === 'Edge' && env.version >= 79;

  // Prior versions ofo Edge versions were based on a proprietary, non-Chromium
  // code base
  env.isLegacyEdge = env.name === 'Edge' && env.version < 79;

  env.isLegacyElectron = env.isElectron && env.electronVersion < 16;

  env.isLegacyChrome = env.isChrome && env.version < 87;

  env.isLegacyFirefox = env.isFirefox && env.version <= 98;

  env.isChromium = !!(
    env.isChrome ||
    env.isChromiumEdge ||
    env.isElectron ||
    env.isOpera ||
    env.isSamsung
  );

  env.isAndroid = env.userAgent.indexOf('android') > -1;

  env.userAgent = navigator.userAgent;

  // eslint-disable-next-line no-undef
  env.protocol = location.protocol;
  // eslint-disable-next-line no-undef
  env.hostName = location.hostName;

  const regex = new RegExp(/(iPad|iPhone|iPod|android|webOS)/i);
  env.isMobileDevice = regex.test(env.userAgent);

  env.isProd = isProd();
  env.isRel = isRel();
  env.isDev = isDev();

  if (env.version === -1 || env.name === 'unknown' || (/iPhone|iPad/.test(env.userAgent) && !env.iOSVersion) || (env.isAndroidWebView && !env.androidVersion)) {
    env.error = true;
  }
};

const parse = (navigator) => {
  // We clear env since we have tests which pass in mocked user agent strings.
  resetEnv();

  if (isNode()) {
    parseNode();
  } else {
    parseBrowser(navigator);
  }

  return env;
};

export default {
  parse,
  isNode,
};
